/**
 * URL-safe Base64 encoding/decoding utilities
 *
 * Standard base64 uses characters (+, /, =) that are not URL-safe and can cause
 * issues in URL paths, particularly on mobile browsers (Safari, Samsung Internet).
 *
 * This service provides URL-safe base64 encoding/decoding that:
 * - Replaces + with -
 * - Replaces / with _
 * - Strips trailing = padding (re-added on decode)
 */

/**
 * Encode a string to URL-safe base64
 * @param {string} str - The string to encode
 * @returns {string} URL-safe base64 encoded string
 */
export const encodeBase64Url = (str) => {
  const base64 = window.btoa(str);
  return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
};

/**
 * Decode a URL-safe base64 string (also handles standard base64)
 * @param {string} str - The URL-safe base64 string to decode
 * @returns {string} Decoded string
 */
export const decodeBase64Url = (str) => {
  // First decode any URL encoding, then convert URL-safe base64 chars back to standard
  let decoded = decodeURIComponent(str).replace(/-/g, '+').replace(/_/g, '/');
  // Add padding if stripped (base64 strings must be divisible by 4)
  while (decoded.length % 4) {
    decoded += '=';
  }
  return window.atob(decoded);
};

/**
 * Encode an object to URL-safe base64 JSON
 * @param {object} obj - The object to encode
 * @returns {string} URL-safe base64 encoded JSON string
 */
export const encodeObjectToBase64Url = (obj) => {
  return encodeBase64Url(JSON.stringify(obj));
};

/**
 * Decode a URL-safe base64 string to an object
 * @param {string} str - The URL-safe base64 string to decode
 * @returns {object} Decoded object
 */
export const decodeBase64UrlToObject = (str) => {
  return JSON.parse(decodeBase64Url(str));
};
